import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, Paper, Typography, useTheme } from "@mui/material";
import {
  Autocomplete,
  Button,
  ButtonGroup,
  DateRangePicker,
  Toolbar,
} from "common";
import BoatCheckboxSelector from "components/BoatCheckboxSelector/BoatCheckboxSelector";
import ProgramCheckboxSelector from "components/ProgramCheckboxSelector/ProgramCheckboxSelector";
import useDictionaries from "features/dictionaries/useDictionaries";
import useAccess from "features/profile/useAccess";
import useSettings from "features/settings/useSettings";
import { usePivotReportContext } from "../usePivotReport";

export interface PivotReportFilterProps {}

const PivotReportFilter: React.FC<PivotReportFilterProps> = ({}) => {
  const { filter, setFilter, params, setParams, data } =
    usePivotReportContext();
  const { hasPermissions } = useAccess();
  const { dictionaries } = useDictionaries();
  const theme = useTheme();
  const [settings, setSettings] = useSettings({ key: "pivotReport" });

  // Report type options - these could also come from an API
  const reportTypeOptions = [
    { value: "boat", label: "Hajó" },
    { value: "event", label: "Program" },
    { value: "booking", label: "Foglalás" },
  ];

  // Get dynamic options from data config
  const dimensionOptions = data?.config?.available_dimensions
    ? Object.entries(data.config.available_dimensions).map(
        ([value, label]) => ({
          value,
          label,
        })
      )
    : [];

  const measureOptions = data?.config?.available_measures
    ? Object.entries(data.config.available_measures).map(
        ([value, measure]) => ({
          value,
          label: measure.label,
          type: measure.type,
          format: measure.format,
          colors: measure.colors,
        })
      )
    : [];

  // Helper function to get measure button colors
  const getMeasureButtonSx = (selectedMeasure?: string | null) => {
    if (!selectedMeasure) return undefined;

    const measure = measureOptions.find(m => m.value === selectedMeasure);
    if (!measure?.colors) return undefined;

    return {
      "& .MuiButton-contained": {
        backgroundColor:
          theme.palette.mode === "dark"
            ? measure.colors.dark
            : measure.colors.light,
        "&:hover": {
          backgroundColor: measure.colors.base,
        },
      },
    };
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Paper
        sx={{
          p: 1,
          display: "flex",
          alignItems: "center",
          flex: 1,
          alignSelf: "stretch",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            p: 1,
            display: "flex",
            alignItems: "center",
            flex: 1,
            alignSelf: "stretch",
            justifyContent: "center",
            gap: 2,
            flexWrap: "wrap",
            flexDirection: "column",
          }}
        >
          {/* Report Type Selector */}
          <ButtonGroup
            label="Riport Típus"
            size="small"
            options={reportTypeOptions}
            value={filter.reportType}
            onChange={(evt: any) => {
              const newReportType = evt.target.value as
                | "event"
                | "boat"
                | "booking";
              // Get saved params for the new report type
              const savedParams = settings?.[newReportType];

              setFilter({
                ...filter,
                reportType: newReportType,
                // Load saved params for the new report type
                rowDimension: savedParams?.rowDimension,
                columnDimension: savedParams?.columnDimension,
                primaryMeasure: savedParams?.primaryMeasure,
                secondaryMeasure: savedParams?.secondaryMeasure,
              });
            }}
          />

          {/* Date Range */}
          <DateRangePicker
            sx={{ width: 200 }}
            defaultRangeIdStorageKey="pivotReportFilterDateRange"
            value={{ from: filter.from, to: filter.to }}
            onChange={range => {
              setFilter({
                ...filter,
                ...(range as any),
              });
            }}
          />

          {/* Pivot Configuration - only show if we have config data */}
          {data?.config &&
            dimensionOptions.length > 0 &&
            measureOptions.length > 0 && (
              <Toolbar sx={{ alignSelf: "stretch", gap: 2, flexWrap: "wrap" }}>
                <ButtonGroup
                  label="Sorok"
                  size="small"
                  options={dimensionOptions}
                  value={params.rowDimension}
                  onChange={(evt: any) => {
                    setParams({
                      rowDimension: evt.target.value,
                    });
                  }}
                />
                <ButtonGroup
                  label="Oszlopok"
                  size="small"
                  options={dimensionOptions}
                  value={params.columnDimension}
                  onChange={(evt: any) => {
                    setParams({
                      columnDimension: evt.target.value,
                    });
                  }}
                />
                <Box>
                  <ButtonGroup
                    label="Mérőszám"
                    size="small"
                    options={measureOptions}
                    value={params.primaryMeasure}
                    onChange={(evt: any) => {
                      console.log(
                        "Primary measure onChange:",
                        evt.target.value
                      );
                      setParams({
                        primaryMeasure: evt.target.value,
                      });
                    }}
                    sx={getMeasureButtonSx(params.primaryMeasure)}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      px: 1,
                      color: theme.palette.text.secondary,
                      fontSize: "0.7rem",
                      fontStyle: "italic",
                    }}
                  >
                    {measureOptions.find(m => m.value === params.primaryMeasure)
                      ?.label || ""}
                  </Typography>
                </Box>
                <Box>
                  <ButtonGroup
                    label="Másodlagos mérőszám"
                    size="small"
                    options={measureOptions}
                    value={params.secondaryMeasure}
                    onChange={(evt: any) => {
                      console.log(
                        "Secondary measure onChange:",
                        evt.target.value,
                        "params:",
                        params
                      );
                      const value = evt.target.value;
                      setParams({
                        secondaryMeasure:
                          params.secondaryMeasure === value ? null : value,
                      });
                    }}
                    sx={getMeasureButtonSx(params.secondaryMeasure)}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.5,
                      px: 1,
                      color: theme.palette.text.secondary,
                      fontSize: "0.7rem",
                      fontStyle: "italic",
                    }}
                  >
                    {params.secondaryMeasure
                      ? measureOptions.find(
                          m => m.value === params.secondaryMeasure
                        )?.label || ""
                      : "Nincs másodlagos mérőszám kiválasztva"}
                  </Typography>
                </Box>
              </Toolbar>
            )}
        </Box>

        <Button
          variant="text"
          size="small"
          endIcon={settings.show_filter ? <ExpandLess /> : <ExpandMore />}
          title={settings.show_filter ? "Szűrő elrejtése" : "Szűrő mutatása"}
          onClick={() => {
            setSettings({ show_filter: !settings.show_filter });
          }}
        />
        <Collapse in={settings.show_filter} sx={{ maxWidth: "100%" }}>
          {/* Partner and Program Filters */}
          <Toolbar
            sx={{ alignSelf: "stretch", gap: 2, flexWrap: "wrap", mt: 2 }}
          >
            {data?.config?.available_filters?.includes("partner_ids") && (
              <Autocomplete
                sx={{ flex: 1 }}
                label="Partnerek"
                multiple
                options={dictionaries.partners}
                value={filter.partnerIds}
                onChange={(e: any) =>
                  setFilter({
                    ...filter,
                    partnerIds: e.target.value,
                  })
                }
              />
            )}
            {data?.config?.available_filters?.includes("partner_group_ids") && (
              <Autocomplete
                sx={{ flex: 1 }}
                label="Partner csoportok"
                multiple
                options={dictionaries.partnergroups}
                value={filter.partnerGroupIds}
                onChange={(e: any) =>
                  setFilter({
                    ...filter,
                    partnerGroupIds: e.target.value,
                  })
                }
              />
            )}
          </Toolbar>
          <Box
            sx={{
              p: 1,
              flex: 1,
              alignSelf: "stretch",
              py: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {data?.config?.available_filters?.includes("boat_ids") && (
              <BoatCheckboxSelector
                color={theme.palette.primary.main}
                value={filter.boatIds || []}
                onChange={(evt: any) =>
                  setFilter({
                    ...filter,
                    boatIds: (evt.target?.value
                      ? evt.target?.value
                      : []) as any,
                  })
                }
              />
            )}
            {data?.config?.available_filters?.includes("program_ids") && (
              <ProgramCheckboxSelector
                color={theme.palette.secondary.main}
                value={filter.programIds || []}
                onChange={(evt: any) =>
                  setFilter({
                    ...filter,
                    programIds: (evt.target?.value
                      ? evt.target?.value
                      : []) as any,
                  })
                }
              />
            )}
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};
export default PivotReportFilter;
